import { createContext, useContext, useState, useCallback, useEffect } from "react";

export const UI = {
  nav: {
    home: { de: "Start", en: "Home" },
    work: { de: "Projekte", en: "Work" },
    services: { de: "Leistungen", en: "Services" },
    products: { de: "Produkte", en: "Products" },
    contact: { de: "Kontakt", en: "Contact" },
  },
  cta: {
    viewWork: { de: "Projekte ansehen", en: "View my work" },
    startProject: { de: "Projekt starten", en: "Start a project" },
    letsStart: { de: "Lass uns starten", en: "Let's start" },
    caseStudy: { de: "Case Study", en: "Case study" },
    view: { de: "Ansehen", en: "View" },
    buy: { de: "Kaufen", en: "Buy" },
    download: { de: "Herunterladen", en: "Download" },
    back: { de: "Zurück zu Arbeiten", en: "Back to work" },
    liveSoon: { de: "Link folgt", en: "Link coming soon" },
    liveProduct: { de: "Live-Produkt ansehen", en: "View live product" },
    send: { de: "Nachricht senden", en: "Send message" },
    sending: { de: "Wird gesendet…", en: "Sending…" },
    sendAnother: { de: "Weitere Nachricht senden", en: "Send another message" },
  },
  home: {
    heroLines: {
      de: ["Von der Idee", "zum fertigen", "Produkt."],
      en: ["From idea", "to finished", "product."],
    },
    heroSub: {
      de: "Strategie, Design, Build, Publishing und Launch — zusammengeführt in einen vollständigen Workflow.",
      en: "Strategy, design, build, publishing and launch — brought together into one complete workflow.",
    },
    heroChips: {
      de: ["Websites", "Apps", "Bücher", "Digitale Produkte"],
      en: ["Websites", "Apps", "Books", "Digital Products"],
    },
    availability: { de: "Verfügbar für neue Projekte", en: "Available for new projects" },
    selectedWork: { de: "Ausgewählte Arbeiten", en: "Selected Work" },
    selectedWorkSub: {
      de: "Eine Auswahl an Websites, Apps, Büchern und digitalen Produkten.",
      en: "A selection of websites, apps, books and digital products.",
    },
    whatIBuild: { de: "Was ich baue", en: "What I Build" },
    howIWork: { de: "Wie ich arbeite", en: "How I Work" },
    howIWorkSub: { de: "Ein Prozess in fünf Schritten.", en: "A process in five steps." },
    whyTitle: { de: "Warum mit mir arbeiten", en: "Why Work With Me" },
    whyStatement: {
      de: "Ich höre nicht beim Konzept auf. Ich bringe Projekte in einen nutzbaren, veröffentlichbaren, launch-fertigen Zustand.",
      en: "I don't stop at the concept. I take projects to a usable, publishable, launch-ready state.",
    },
    finalTitle: { de: "Eine Idee, die es wert ist, gebaut zu werden?", en: "Have an idea worth building?" },
    finalText: {
      de: "Sag mir, was du erschaffen willst. Ich helfe dir, es in ein echtes Produkt zu verwandeln.",
      en: "Tell me what you want to create. I'll help turn it into a real product.",
    },
    marquee: {
      de: "Websites · Apps · Bücher · Produkte · Von der Idee zum Launch ·",
      en: "Websites · Apps · Books · Products · From idea to launch ·",
    },
  },
  work: {
    title: { de: "Projekte", en: "Work" },
    intro: {
      de: "Projekte über Websites, Apps, Publishing und digitale Produkte.",
      en: "Projects across websites, apps, publishing and digital products.",
    },
  },
  caseStudy: {
    overview: { de: "Überblick", en: "Overview" },
    problem: { de: "Problem", en: "Problem" },
    approach: { de: "Ansatz", en: "Approach" },
    built: { de: "Was ich gebaut habe", en: "What I built" },
    tech: { de: "Technologie / Tools", en: "Technology / tools" },
    gallery: { de: "Galerie", en: "Gallery" },
    result: { de: "Ergebnis", en: "Final result" },
    next: { de: "Nächstes Projekt", en: "Next project" },
  },
  services: {
    title: { de: "Leistungen", en: "Services" },
    intro: {
      de: "Vier klare Wege, eine Idee in ein echtes, launch-fertiges Produkt zu verwandeln.",
      en: "Four clear ways to turn an idea into a real, launch-ready product.",
    },
    build: { de: "Was ich baue", en: "What I build" },
    who: { de: "Für wen", en: "Who it's for" },
    deliver: { de: "Was du bekommst", en: "What you get" },
  },
  products: {
    title: { de: "Produkte", en: "Products" },
    intro: {
      de: "Meine eigenen Produkte — Bücher, Kurse und Apps.",
      en: "My own products — books, courses and apps.",
    },
    note: {
      de: "Verfügbar auf Amazon, Gumroad & im Google Play Store.",
      en: "Available on Amazon, Gumroad & the Google Play Store.",
    },
  },
  contact: {
    title: { de: "Lass uns etwas bauen.", en: "Let's build something." },
    intro: {
      de: "Erzähl mir kurz von deiner Idee. Ich melde mich zurück.",
      en: "Tell me briefly about your idea. I'll get back to you.",
    },
    name: { de: "Name", en: "Name" },
    email: { de: "E-Mail", en: "Email" },
    build: { de: "Was willst du bauen?", en: "What do you want to build?" },
    budget: { de: "Budget-Rahmen", en: "Budget range" },
    message: { de: "Nachricht", en: "Message" },
    budgetPlaceholder: { de: "Bitte wählen", en: "Please select" },
    success: { de: "Danke — deine Nachricht ist angekommen.", en: "Thanks — your message has been received." },
    error: { de: "Etwas ist schiefgelaufen. Bitte versuche es erneut.", en: "Something went wrong. Please try again." },
    buildOptions: {
      de: ["Website", "App", "Digitales Produkt", "Buch / KDP", "Sonstiges"],
      en: ["Website", "App", "Digital product", "Book / KDP", "Other"],
    },
    budgetOptions: ["< €2.000", "€2.000 – €5.000", "€5.000 – €10.000", "€10.000+"],
  },
  footer: {
    tagline: {
      de: "Von der Idee zum echten Produkt zum Launch.",
      en: "From idea to real product to launch.",
    },
    rights: { de: "Alle Rechte vorbehalten.", en: "All rights reserved." },
    build: { de: "Ein Projekt starten", en: "Start a project" },
    impressumNote: { de: "Impressum-Daten folgen.", en: "Impressum details to follow." },
    impressum: { de: "Impressum", en: "Legal notice" },
  },
  whatsapp: {
    fabLabel: { de: "Schreib mir auf WhatsApp", en: "Message me on WhatsApp" },
    cardTitle: { de: "Lieber per WhatsApp?", en: "Prefer WhatsApp?" },
    cardIntro: {
      de: "Schreib deine Nachricht — sie öffnet sich direkt in WhatsApp.",
      en: "Write your message — it opens straight in WhatsApp.",
    },
    placeholder: { de: "Deine Nachricht…", en: "Your message…" },
    send: { de: "Auf WhatsApp senden", en: "Send on WhatsApp" },
    or: { de: "oder", en: "or" },
  },
  cookie: {
    text: {
      de: "Wir verwenden Cookies für eine anonyme Reichweitenanalyse, um die Website zu verbessern.",
      en: "We use cookies for anonymous analytics to improve the website.",
    },
    accept: { de: "Akzeptieren", en: "Accept" },
    reject: { de: "Ablehnen", en: "Reject" },
    more: { de: "Mehr erfahren", en: "Learn more" },
  },
  testimonials: {
    eyebrow: { de: "Kundenstimmen", en: "Client voices" },
    title: { de: "Was Kunden sagen", en: "What clients say" },
    previewNote: {
      de: "Noch keine Bewertungen — seien Sie die erste Stimme.",
      en: "No reviews yet — be the first voice.",
    },
    writeBtn: { de: "Bewertung schreiben", en: "Write a review" },
    formTitle: { de: "Ihre Bewertung", en: "Your review" },
    formIntro: {
      de: "Teilen Sie Ihre Erfahrung. Nach kurzer Prüfung erscheint Ihre Bewertung öffentlich.",
      en: "Share your experience. After a short review it will appear publicly.",
    },
    fName: { de: "Name", en: "Name" },
    fCompany: { de: "Firma (optional)", en: "Company (optional)" },
    fEmail: { de: "E-Mail (optional)", en: "Email (optional)" },
    emailNote: {
      de: "Nur für eine Bestätigung, sobald Ihre Bewertung freigeschaltet ist. Wird nicht öffentlich angezeigt.",
      en: "Only for a confirmation once your review is approved. Never shown publicly.",
    },
    fService: { de: "Projektart", en: "Project type" },
    fRating: { de: "Bewertung", en: "Rating" },
    fQuote: { de: "Ihre Nachricht", en: "Your message" },
    submit: { de: "Absenden", en: "Submit" },
    sending: { de: "Wird gesendet…", en: "Sending…" },
    cancel: { de: "Abbrechen", en: "Cancel" },
    close: { de: "Schließen", en: "Close" },
    successTitle: { de: "Vielen Dank!", en: "Thank you!" },
    successBody: {
      de: "Ihre Bewertung wurde übermittelt und wird nach Prüfung veröffentlicht.",
      en: "Your review was submitted and will be published after review.",
    },
    error: {
      de: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
      en: "Something went wrong. Please try again.",
    },
    filterAll: { de: "Alle", en: "All" },
    services: {
      Website: { de: "Websites", en: "Websites" },
      App: { de: "Apps", en: "Apps" },
      "Digital Product": { de: "Digitale Produkte", en: "Digital Products" },
      "Book / Publishing": { de: "Bücher", en: "Books" },
      Other: { de: "Sonstiges", en: "Other" },
    },
  },
  seo: {
    home: {
      title: { de: "WERKVERDUNG — Von der Idee zum fertigen Produkt", en: "WERKVERDUNG — From idea to finished product" },
      desc: {
        de: "Digital Product Builder aus Fürth: Websites, Apps, Bücher und digitale Produkte — gebaut bis zum Launch.",
        en: "Digital product builder from Fürth: websites, apps, books and digital products — built to launch.",
      },
    },
    work: {
      title: { de: "Projekte — WERKVERDUNG", en: "Work — WERKVERDUNG" },
      desc: {
        de: "Ausgewählte Projekte: Websites, Apps und Buchreihen, von der Idee bis zum Launch umgesetzt.",
        en: "Selected work: websites, apps and book series, delivered from idea to launch.",
      },
    },
    services: {
      title: { de: "Leistungen — WERKVERDUNG", en: "Services — WERKVERDUNG" },
      desc: {
        de: "Leistungen: Webdesign & Entwicklung, App-Design, Buch- & Coverdesign und digitale Produkte.",
        en: "Services: web design & development, app design, book & cover design and digital products.",
      },
    },
    products: {
      title: { de: "Produkte — WERKVERDUNG", en: "Products — WERKVERDUNG" },
      desc: {
        de: "Eigene Produkte: Ausmalbücher, Lyrik, Romane und Kurse — verfügbar auf Amazon, Gumroad & mehr.",
        en: "Own products: coloring books, poetry, novels and courses — available on Amazon, Gumroad & more.",
      },
    },
    contact: {
      title: { de: "Kontakt — WERKVERDUNG", en: "Contact — WERKVERDUNG" },
      desc: {
        de: "Lass uns dein Projekt starten — per Formular oder direkt über WhatsApp.",
        en: "Let's start your project — via the form or directly on WhatsApp.",
      },
    },
  },
};

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("wv_lang") || "de";
    } catch (e) {
      return "de";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("wv_lang", lang);
    } catch (e) {
      /* localStorage unavailable (private mode) */
    }
  }, [lang]);
  const toggle = useCallback(() => setLang((l) => (l === "de" ? "en" : "de")), []);
  const t = useCallback((obj) => (obj && typeof obj === "object" ? obj[lang] ?? obj.en : obj), [lang]);
  return (
    <LangContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}
